import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  adminListSubCategories, 
  adminDeleteSubCategory, 
  adminListCategories 
} from "../api/admin";
import SubCategoryTable from "../components/SubCategoryTable";
import SubCategoryForm from "../components/SubCategoryForm";
import "./SubCategoryPage.css";
import { toast } from "react-toastify";

const SubCategoryPage = ({ categoryId: propCategoryId }) => {
    const { categoryId: paramCategoryId } = useParams();
    const initialCategoryId = propCategoryId || paramCategoryId || "";
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState(initialCategoryId);
    const [subcategories, setSubcategories] = useState([]);
    const [, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingSubCategory, setEditingSubCategory] = useState(null);
    
    const role = localStorage.getItem("adminRole");
    const permissions = JSON.parse(localStorage.getItem("adminPermissions") || "[]");

    const canWrite = role === "admin" || permissions.includes("Subcategories:write");

    // Fetch all categories for the dropdown
    const fetchCategories = useCallback(async () => {
        try {
            const res = await adminListCategories();
            setCategories(res.data || []);
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    }, []);

    // Fetch subcategories and filter by category
    const fetchSubCategories = useCallback(async () => {
        setLoading(true);
        try {
            const res = await adminListSubCategories();
            const allSubs = Array.isArray(res.data) ? res.data : [];
            if (selectedCategoryId) {
                const filtered = allSubs.filter(sub => 
                    (sub.category_id || sub.parent_category_id) === selectedCategoryId
                );
                setSubcategories(filtered);
            } else {
                setSubcategories(allSubs);
            }
        } catch (error) {
            console.error("Error fetching subcategories:", error);
            toast.error("Failed to fetch subcategories.");
            setSubcategories([]);
        } finally {
            setLoading(false);
        }
    }, [selectedCategoryId]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    useEffect(() => {
        fetchSubCategories();
    }, [fetchSubCategories]);

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this subcategory?")) return;
        try {
            await adminDeleteSubCategory(id);
            toast.success("Subcategory deleted! 🗑️");
            fetchSubCategories();
        } catch (error) {
            toast.error("Failed to delete subcategory.");
        }
    };

    const handleEdit = (sub) => {
        setEditingSubCategory(sub);
        setShowForm(true);
    };

    const clearEdit = () => {
        setEditingSubCategory(null);
        setShowForm(false);
    };

    const selectedCategoryName = categories.find(c => 
        String(c.category_id || c._id || c.id) === String(selectedCategoryId)
    )?.category_name;

    return (
        <div className="admin-page">
            {!showForm ? (
                <>
                    <div className="page-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <button className="back-btn" onClick={() => navigate("/adminDashboard/categories")}>←</button>
                            <h2 className="adminproducts">Subcategories</h2>
                        </div>
                        <div className="header-actions">
                            <div className="search-container">
                                <span className="search-label">Category:</span>
                                <select
                                    value={selectedCategoryId}
                                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                                    className="user-search-input"
                                    style={{ fontWeight: "bold" }}
                                >
                                    <option value="">-- All Categories --</option>
                                    {categories.map((c) => {
                                        const catId = c.category_id || c._id || c.id;
                                        return (
                                            <option key={catId} value={catId}>
                                                {c.category_name}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                            {selectedCategoryId && canWrite && (
                                <button className="btn-add-product" onClick={() => setShowForm(true)}>
                                    + Add Subcategory
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="subcategory-display-section">
                        {selectedCategoryId ? (
                                <SubCategoryTable
                                    subcategories={subcategories}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                    canWrite={canWrite}
                                />
                        ) : (
                            <div className="no-selection-placeholder">
                                <p>📋 Select a category above to manage its subcategories.</p>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <div className="page-header">
                        <div className="header-with-back">
                            <button className="back-btn" onClick={clearEdit}>← Back</button>
                            <h2 className="adminproducts">
                                {editingSubCategory ? "Update Subcategory" : "Create New Subcategory"}
                                {selectedCategoryName && <span style={{ color: '#666', fontSize: '0.9rem' }}> to {selectedCategoryName}</span>}
                            </h2>
                        </div>
                    </div>
                    <div className="form-inline-container">
                        <SubCategoryForm
                            parentCategoryId={selectedCategoryId}
                            subcategory={editingSubCategory}
                            onSuccess={() => {
                                fetchSubCategories();
                                clearEdit();
                            }}
                            onCancel={clearEdit}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default SubCategoryPage;

